import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { canRead, canWrite } from '../../utils/roles';
import DayBlock from './DayBlock';
import EventDrawer from './EventDrawer';
import TaskDrawer from './TaskDrawer';
import ChecklistPage from '../ChecklistPage';
import AgendaResponsiveView from './AgendaResponsiveView';
import AgendaSyncStatus from './AgendaSyncStatus';
import AgendaMoveDateModal from './AgendaMoveDateModal';
import AgendaSectionTabs from './AgendaSectionTabs';
import AgendaFocusedView from './AgendaFocusedView';
import useAgendaData from './useAgendaData';
import useAgendaSearch, { SEARCH_DIMENSIONS, DEFAULT_DIMENSIONS } from './useAgendaSearch';
import AgendaSearchResults from './AgendaSearchResults';
import useBackToClose from '../../hooks/useBackToClose';
import { addDaysStr, currentMonthStr, monthLabel, normalize, operationBaseEventName, todayStr } from './agendaUtils';
import './AgendaTokens.css';
import './AgendaToolbar.css';
import './AgendaGrid.css';
import './AgendaRows.css';
import './AgendaPage.css';
import './AgendaSearchResults.css';

// Dev mock — fallback cuando el backend no corre localmente.
// Solo activo en development (el catch no se dispara en prod si la API responde).
const DEV_MOCK_EVENTS = [
  // Lunes 15
  { id: 10, event_date: '2026-06-15', name: 'Congreso CUTI 2026', place: 'Latu Montevideo', client: 'CUTI', sales_referent: 'Andrés', status: 'confirmado', odoo_code: 'S00871', setup_start: '07:00', start_time: '09:00', end_time: '18:00', team_setup: 'GUILLE · TOTO · PANTALLA ANGIE', team_operation: 'BRAI · AUDIO GRILLO · SUELTO X · FOTÓGRAFO', team_breakdown: 'TOTO · ANGIE', notes: 'Confirmar acceso de carga por rampa lateral', first_armado_date: '2026-06-14', carga_status: 'completed', descarga_status: null },
  { id: 11, event_date: '2026-06-15', name: 'Presentación Tuteur Q2', place: 'Edificio Zonamerica', client: 'Tuteur', sales_referent: 'Pachi', status: 'confirmado', odoo_code: 'S00934', setup_start: '13:00', start_time: '15:00', end_time: '17:30', team_setup: 'ANGIE · CUBA', team_operation: 'CUBA · VIDEÓGRAFO X', team_breakdown: 'ANGIE', notes: null, first_armado_date: null, carga_status: 'in_progress', descarga_status: null },
  { id: 12, event_date: '2026-06-15', name: 'Gala Banco República', place: 'Radisson Victoria Plaza', client: 'BROU', sales_referent: 'Fede', status: 'posible', odoo_code: 'S00955', setup_start: '16:00', start_time: '20:00', end_time: '23:59', team_setup: 'GUILLE · TOTO · BRAI · CUBA · SUELTO X', team_operation: 'BRAI · AUDIO GRILLO · VIDEÓGRAFO X · FOTÓGRAFO · DJ', team_breakdown: 'GUILLE · TOTO · CUBA', notes: 'Evento posible — esperar confirmación del cliente antes del jueves', first_armado_date: '2026-06-14', carga_status: null, descarga_status: null },
  // Martes 16
  { id: 1, event_date: '2026-06-16', name: 'Fenicio E-Show 2026', place: 'Movie Montevideo Shopping', client: 'Fenicio', sales_referent: 'Fede', status: 'confirmado', odoo_code: 'S01589', setup_start: '23:59', start_time: '08:30', end_time: '13:30', team_setup: 'SALA GUILLE · PANTALLA ANGIE · CUBA · CÁMARA WORKSHOP X', team_operation: 'BRAI · AUDIO GRILLO · SUELTO X · VIDEÓGRAFO X · FOTÓGRAFO', team_breakdown: 'SALA GUILLE · ANGIE · BRAI', notes: 'Confirmar pase libre con seguridad', first_armado_date: '2026-06-15', carga_status: 'completed', descarga_status: 'in_progress' },
  { id: 2, event_date: '2026-06-16', name: 'Alquiler Stand Data4Sales', place: 'Movie Montevideo Shopping', client: 'Data4Sales', sales_referent: 'Fede', status: 'confirmado', odoo_code: 'S02191', setup_start: null, start_time: '08:30', end_time: '13:30', team_setup: null, team_operation: null, team_breakdown: null, notes: null, first_armado_date: null, carga_status: null, descarga_status: null },
  { id: 3, event_date: '2026-06-16', name: 'Workshop Ágil — Scrum Masters UY', place: 'WeWork WTC', client: 'Agile UY', sales_referent: 'Andrés', status: 'confirmado', odoo_code: 'S02204', setup_start: '07:30', start_time: '09:00', end_time: '13:00', team_setup: 'TOTO · ANGIE', team_operation: 'TOTO · SUELTO X', team_breakdown: 'TOTO', notes: null, first_armado_date: '2026-06-15', carga_status: 'completed', descarga_status: null },
  { id: 4, event_date: '2026-06-16', name: 'Cumbre Latam Retail 2026', place: 'Antel Arena', client: 'Retail UY', sales_referent: 'Pachi', status: 'confirmado', odoo_code: 'S02310', setup_start: '06:00', start_time: '10:00', end_time: '19:00', team_setup: 'GUILLE · BRAI · CUBA · PANTALLA ANGIE · TOTO · SUELTO X', team_operation: 'BRAI · AUDIO GRILLO · CUBA · VIDEÓGRAFO X · FOTÓGRAFO · DJ · SUELTO X', team_breakdown: 'GUILLE · BRAI · TOTO · ANGIE', notes: 'Plenario en sala principal + 3 salas simultáneas. Coordinar con producción de Antel Arena.', first_armado_date: '2026-06-15', carga_status: 'in_progress', descarga_status: null },
  // Miércoles 17
  { id: 5, event_date: '2026-06-17', name: 'Lanzamiento Producto ANCAP', place: 'Hotel Cottage', client: 'ANCAP', sales_referent: 'Fede', status: 'confirmado', odoo_code: 'S02401', setup_start: '14:00', start_time: '19:00', end_time: '22:00', team_setup: 'ANGIE · CUBA', team_operation: 'CUBA · BRAI · FOTÓGRAFO', team_breakdown: 'ANGIE · CUBA', notes: null, first_armado_date: null, carga_status: null, descarga_status: null },
  { id: 6, event_date: '2026-06-17', name: 'Capacitación interna Deloitte', place: 'Torre Deloitte Pocitos', client: 'Deloitte', sales_referent: 'Andrés', status: 'confirmado', odoo_code: 'S02388', setup_start: '08:30', start_time: '09:00', end_time: '12:00', team_setup: 'TOTO', team_operation: 'TOTO', team_breakdown: 'TOTO', notes: 'Solo pantalla y micrófono inalámbrico. Acceso por recepción nivel 2.', first_armado_date: null, carga_status: null, descarga_status: null },
  { id: 7, event_date: '2026-06-17', name: 'Cena Gala Fundación Peluffo', place: 'Museo Nacional de Artes Visuales', client: 'Fundación Peluffo', sales_referent: 'Pachi', status: 'posible', odoo_code: 'S02422', setup_start: '15:00', start_time: '20:30', end_time: '23:59', team_setup: 'GUILLE · PANTALLA ANGIE · CUBA · TOTO', team_operation: 'BRAI · AUDIO GRILLO · VIDEÓGRAFO X · FOTÓGRAFO', team_breakdown: 'GUILLE · TOTO', notes: null, first_armado_date: null, carga_status: null, descarga_status: null },
  // Jueves 18
  { id: 8, event_date: '2026-06-18', name: 'Congreso Médico 2026', place: 'Sheraton Montevideo', client: 'SMU', sales_referent: 'Fede', status: 'confirmado', odoo_code: 'S02500', setup_start: '06:00', start_time: '08:00', end_time: '20:00', team_setup: 'GUILLE · BRAI · ANGIE · CUBA · TOTO · SUELTO X', team_operation: 'BRAI · AUDIO GRILLO · CUBA · VIDEÓGRAFO X · FOTÓGRAFO · SUELTO X', team_breakdown: 'GUILLE · BRAI · TOTO · ANGIE', notes: '4 salas en simultáneo. Sala plenaria + 3 talleres. Confirmar acceso a estacionamiento para camión.', first_armado_date: '2026-06-17', carga_status: 'completed', descarga_status: null },
  { id: 9, event_date: '2026-06-18', name: 'Desfile Moda Primavera', place: 'Espacio Industria', client: 'Moda UY', sales_referent: 'Pachi', status: 'confirmado', odoo_code: 'S02511', setup_start: '12:00', start_time: '18:00', end_time: '21:00', team_setup: 'PANTALLA ANGIE · CUBA', team_operation: 'AUDIO GRILLO · VIDEÓGRAFO X · FOTÓGRAFO', team_breakdown: 'ANGIE', notes: null, first_armado_date: null, carga_status: null, descarga_status: null },
];
const DEV_MOCK_TASKS = [
  { id: 1, task_date: '2026-06-15', title: 'Retirar generador del depósito', status: 'finalizado', owner: 'Toto', time: '07:00', end_time: '08:00', notes: 'Coordinar con logística', event_name: 'Congreso CUTI 2026' },
  { id: 2, task_date: '2026-06-15', title: 'Confirmar catering con proveedor', status: 'pendiente', owner: 'Pachi', time: '10:00', end_time: null, notes: null, event_name: null },
  { id: 3, task_date: '2026-06-15', title: 'Entregar equipo de grabación', status: 'pendiente', owner: 'Guille', time: '12:00', end_time: '13:00', notes: 'Cámara Sony + trípode + 2 baterías extra', event_name: 'Presentación Tuteur Q2' },
  { id: 4, task_date: '2026-06-16', title: 'Retirar auriculares Pro Audio', status: 'pendiente', owner: 'Guille', time: '10:00', end_time: '11:00', notes: 'Solicitar a bodega central', event_name: 'Evento Tuteur' },
  { id: 5, task_date: '2026-06-16', title: 'Cargar van con cable serpiente', status: 'pendiente', owner: 'Brai', time: '06:30', end_time: '07:00', notes: null, event_name: 'Cumbre Latam Retail 2026' },
  { id: 6, task_date: '2026-06-16', title: 'Comprar pilas AA para controles', status: 'finalizado', owner: 'Toto', time: '08:00', end_time: null, notes: '2 paquetes de 8', event_name: null },
  { id: 7, task_date: '2026-06-17', title: 'Devolver cable XLR a bodega', status: 'pendiente', owner: 'Brai', time: '09:00', end_time: '09:30', notes: null, event_name: null },
  { id: 8, task_date: '2026-06-17', title: 'Revisar sistema de iluminación', status: 'pendiente', owner: 'Guille', time: '14:00', end_time: '15:00', notes: 'Testear antes de la gala', event_name: 'Cena Gala Fundación Peluffo' },
  { id: 9, task_date: '2026-06-18', title: 'Preparar maletín de emergencia', status: 'pendiente', owner: 'Toto', time: '05:30', end_time: '06:00', notes: 'Cables, pilas, adaptadores, DI box de repuesto', event_name: 'Congreso Médico 2026' },
  { id: 10, task_date: '2026-06-18', title: 'Facturar evento Fenicio', status: 'pendiente', owner: 'Pachi', time: null, end_time: null, notes: null, event_name: 'Fenicio E-Show 2026' },
];
const DEV_MOCK_ARMADOS = [
  { id: 1, armado_date: '2026-06-14', event_id: 10, event_name: 'Congreso CUTI 2026', event_place: 'Latu Montevideo', event_date: '2026-06-15', start_time: '08:00', end_time: '12:00', assigned_people: 'GUILLE · TOTO · ANGIE', notes: 'Montar escenario y pantalla principal' },
  { id: 2, armado_date: '2026-06-14', event_id: 12, event_name: 'Gala Banco República', event_place: 'Radisson Victoria Plaza', event_date: '2026-06-15', start_time: '15:00', end_time: '19:00', assigned_people: 'BRAI · CUBA', notes: null },
  { id: 3, armado_date: '2026-06-15', event_id: 1, event_name: 'Fenicio E-Show 2026', event_place: 'Movie Montevideo Shopping', event_date: '2026-06-16', start_time: '09:00', end_time: '13:00', assigned_people: 'SALA GUILLE · PANTALLA ANGIE', notes: null },
  { id: 4, armado_date: '2026-06-15', event_id: 3, event_name: 'Workshop Ágil — Scrum Masters UY', event_place: 'WeWork WTC', event_date: '2026-06-16', start_time: '14:00', end_time: '16:00', assigned_people: 'TOTO · ANGIE', notes: 'Solo pantalla y 2 micrófonos' },
  { id: 5, armado_date: '2026-06-15', event_id: 4, event_name: 'Cumbre Latam Retail 2026', event_place: 'Antel Arena', event_date: '2026-06-16', start_time: '07:00', end_time: '12:00', assigned_people: 'GUILLE · BRAI · CUBA · PANTALLA ANGIE · TOTO', notes: 'Ingreso por acceso B. Confirmar con producción de Antel Arena.' },
  { id: 6, armado_date: '2026-06-17', event_id: 8, event_name: 'Congreso Médico 2026', event_place: 'Sheraton Montevideo', event_date: '2026-06-18', start_time: '16:00', end_time: '21:00', assigned_people: 'GUILLE · BRAI · ANGIE · CUBA · TOTO', notes: '4 salas — empezar por plenario' },
];

const DEV_FALLBACK_DATA = {
  events: DEV_MOCK_EVENTS,
  tasks: DEV_MOCK_TASKS,
  armados: DEV_MOCK_ARMADOS,
  operations: [],
};


function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

const EVENT_STATUSES = new Set(['confirmado', 'posible', 'cancelado', 'archivado']);
const TASK_STATUSES  = new Set(['pendiente', 'finalizado']);

// Al navegar a equipos de un evento y volver, la Agenda se remonta. Guardamos
// mes + día visible en sessionStorage para restaurar la posición en vez de
// auto-scrollear siempre a HOY.
const AGENDA_LAST_POS_KEY = 'agenda:lastPos';
const AGENDA_LAST_POS_MAX_AGE_MS = 60 * 60 * 1000;

// Lee sin consumir: la Agenda puede montarse/desmontarse varias veces antes
// de aplicar el scroll (switch mobile/desktop, StrictMode); la clave se borra
// recién cuando el scroll restaurado se ejecuta.
function readAgendaLastPos() {
  try {
    const raw = sessionStorage.getItem(AGENDA_LAST_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.month !== 'string' || typeof parsed.day !== 'string'
        || !parsed.ts || Date.now() - parsed.ts > AGENDA_LAST_POS_MAX_AGE_MS) {
      sessionStorage.removeItem(AGENDA_LAST_POS_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export default function AgendaPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // Se calcula una sola vez, en el primer render (el guard evita repetir la
  // lectura/limpieza de sessionStorage en renders posteriores).
  const restoredPosRef = useRef(undefined);
  if (restoredPosRef.current === undefined) {
    restoredPosRef.current = readAgendaLastPos();
  }
  const restoredPos = restoredPosRef.current;
  const canAdmin   = canWrite(user, 'agenda');
  const canOperate = canWrite(user, 'agenda');
  const canViewEquipment = canRead(user, 'agenda.equipos');
  const canViewEventEquipment = canRead(user, 'agenda.equipos.evento');
  const canEditEventEquipment = canWrite(user, 'agenda.equipos.evento');
  const isResponsiveAgenda = useMediaQuery('(max-width: 1024px)');
  const requestedAgendaView = searchParams.get('vista');
  const agendaView = requestedAgendaView === 'armados' || requestedAgendaView === 'desarmados'
    ? 'logistica'
    : ['eventos', 'logistica', 'tareas'].includes(requestedAgendaView)
      ? requestedAgendaView
      : 'general';

  const [month, setMonth]               = useState(() => restoredPos?.month || currentMonthStr());
  const [statusFilter, setStatusFilter] = useState('todos');
  const {
    events, setEvents,
    tasks, setTasks,
    armados, setArmados,
    operations, setOperations,
    personnel,
    lugares,
    clients,
    salesRefs,
    assignmentModel,
    assignmentModelError,
    retryAssignmentModel,
    monthConflicts,
    loading,
    load,
  } = useAgendaData({
    month,
    fallbackData: DEV_FALLBACK_DATA,
    includeArchived: statusFilter === 'archivado',
  });
  const [kbFocus, setKbFocus]           = useState(null); // { type: 'event'|'task'|'armado', id }
  const [expandOverrides, setExpandOverrides] = useState({});
  const [drawerType, setDrawerType]     = useState(null);
  const [drawerTarget, setDrawerTarget] = useState(null);
  const [drawerDate, setDrawerDate]     = useState(null);
  const [drawerInitialData, setDrawerInitialData] = useState(null);
  const [moveDateEvent, setMoveDateEvent] = useState(null);
  const [moveDateTarget, setMoveDateTarget] = useState('');
  const [moveDateSaving, setMoveDateSaving] = useState(false);
  const [operationDatePicker, setOperationDatePicker] = useState(null);
  const [search, setSearch]             = useState('');
  const [searchOpen, setSearchOpen]     = useState(false);   // mobile: búsqueda desplegable
  const [searchDims, setSearchDims]     = useState(DEFAULT_DIMENSIONS);
  const [searchArchived, setSearchArchived] = useState(false);
  const [checklistPanelId, setChecklistPanelId] = useState(null);
  const [checklistPanelEventId, setChecklistPanelEventId] = useState(null);
  const operationsRef = useRef([]);

  useEffect(() => {
    operationsRef.current = operations;
  }, [operations]);

  useEffect(() => {
    if (checklistPanelId === null) return;
    function onKeyDown(e) {
      if (e.key !== 'Escape') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      setChecklistPanelId(null);
      setChecklistPanelEventId(null);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [checklistPanelId]);

  function closeChecklistPanel() { setChecklistPanelId(null); setChecklistPanelEventId(null); }

  // El panel ocupa la pantalla en mobile y ya se cierra con swipe: el gesto de
  // atrás tiene que hacer lo mismo en vez de sacarte de la app.
  useBackToClose(checklistPanelId !== null, closeChecklistPanel);

  // Swipe-derecha (mobile) sobre el drawer del checklist → vuelve a la agenda.
  const drawerRef = useRef(null);
  const drawerSwipe = useRef(null);
  function drawerPointerDown(e) {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    // Excluir sólo campos de texto (un drag horizontal ahí selecciona texto).
    // Los botones NO se excluyen: la detección de eje distingue tap de swipe.
    if (e.target.closest('input, textarea, select')) return;
    drawerSwipe.current = { startX: e.clientX, startY: e.clientY, axis: null, dx: 0 };
  }
  function drawerPointerMove(e) {
    const s = drawerSwipe.current;
    if (!s) return;
    const dx = e.clientX - s.startX, dy = e.clientY - s.startY;
    if (s.axis === null) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      s.axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'h' : 'v';
      if (s.axis === 'v') { drawerSwipe.current = null; return; }
    }
    if (s.axis !== 'h') return;
    const el = drawerRef.current;
    if (!el) return;
    const x = Math.max(0, dx);          // solo hacia la derecha
    s.dx = x;
    el.classList.add('cl-dragging');
    el.style.transform = `translateX(${x}px)`;
    el.style.opacity = String(Math.max(0.4, 1 - x / 500));
  }
  function drawerPointerEnd() {
    const s = drawerSwipe.current;
    drawerSwipe.current = null;
    const el = drawerRef.current;
    if (!s || s.axis !== 'h' || !el) return;
    el.classList.remove('cl-dragging');
    if (s.dx > 90) { closeChecklistPanel(); }
    else { el.style.transform = ''; el.style.opacity = ''; }
  }

  // Auto-scroll al arrastrar cerca de los bordes. El nativo del browser no sube
  // porque el toolbar sticky tapa el borde superior; lo hacemos a mano en ambas
  // direcciones (borde superior = debajo del toolbar).
  useEffect(() => {
    let raf = null, speed = 0, mainEl = null;
    const EDGE = 90, MAX = 26;
    function tick() {
      if (speed && mainEl) { mainEl.scrollTop += speed; raf = requestAnimationFrame(tick); }
      else raf = null;
    }
    function onDragOver(e) {
      mainEl = document.querySelector('.main-content');
      if (!mainEl) return;
      const rect = mainEl.getBoundingClientRect();
      const tb = toolbarRef.current?.getBoundingClientRect();
      const top = (tb && tb.bottom > rect.top ? tb.bottom : rect.top);
      const y = e.clientY;
      // Solo hacia arriba: el nativo del browser ya scrollea bien hacia abajo
      // (duplicarlo lo hace demasiado rápido); arriba falla por el toolbar sticky.
      if (y < top + EDGE) speed = -Math.min(MAX, Math.ceil((top + EDGE - y) / 3));
      else speed = 0;
      if (speed && !raf) raf = requestAnimationFrame(tick);
    }
    function stop() { speed = 0; }
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', stop, true);
    document.addEventListener('dragend', stop, true);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', stop, true);
      document.removeEventListener('dragend', stop, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const kbFocusRef = useRef(null);
  useEffect(() => {
    function scrollRowIntoView(rowEl) {
      const main = document.querySelector('.main-content');
      const cell = Array.from(rowEl.children).find(c => window.getComputedStyle(c).display !== 'contents');
      if (!main || !cell) return;
      const cellRect = cell.getBoundingClientRect();
      const mainRect = main.getBoundingClientRect();
      const pad = 8;
      if (cellRect.top < mainRect.top + pad) {
        main.scrollTop -= (mainRect.top + pad - cellRect.top);
      } else if (cellRect.bottom > mainRect.bottom - pad) {
        main.scrollTop += (cellRect.bottom - mainRect.bottom + pad);
      }
    }

    function onArrow(e) {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.activeElement?.isContentEditable) return;
      e.preventDefault();
      const main = document.querySelector('.main-content');
      const mainRect = main?.getBoundingClientRect();
      const rows = Array.from(document.querySelectorAll('[data-event-id], [data-task-id], [data-armado-id]'))
        .filter(r => !r.closest('.ag-checklist-panel'))
        .filter(r => {
          const c = Array.from(r.children).find(ch => window.getComputedStyle(ch).display !== 'contents');
          return c ? c.getBoundingClientRect().height > 0 : false;
        });
      if (!rows.length) return;
      const cur = kbFocusRef.current;
      const curEl = cur ? document.querySelector(`[data-${cur.type}-id="${cur.id}"]`) : null;
      const curIdx = curEl ? rows.indexOf(curEl) : -1;

      let nextIdx;
      if (curIdx === -1) {
        // Sin foco actual: seleccionar la primera/última fila visible en viewport
        const inViewport = rows.filter(r => {
          const c = Array.from(r.children).find(ch => window.getComputedStyle(ch).display !== 'contents');
          if (!c || !mainRect) return false;
          const rect = c.getBoundingClientRect();
          return rect.bottom > mainRect.top && rect.top < mainRect.bottom;
        });
        if (e.key === 'ArrowDown') {
          nextIdx = inViewport.length ? rows.indexOf(inViewport[0]) : 0;
        } else {
          nextIdx = inViewport.length ? rows.indexOf(inViewport[inViewport.length - 1]) : rows.length - 1;
        }
      } else if (e.key === 'ArrowDown') {
        nextIdx = curIdx < rows.length - 1 ? curIdx + 1 : 0;
      } else {
        nextIdx = curIdx > 0 ? curIdx - 1 : rows.length - 1;
      }
      const next = rows[nextIdx];
      const nextType = next.hasAttribute('data-event-id') ? 'event'
        : next.hasAttribute('data-task-id') ? 'task' : 'armado';
      const nextId = Number(next.getAttribute(`data-${nextType}-id`));
      kbFocusRef.current = { type: nextType, id: nextId };
      setKbFocus({ type: nextType, id: nextId });
      scrollRowIntoView(next);
    }
    function onMouseDown() { setKbFocus(null); kbFocusRef.current = null; }
    document.addEventListener('keydown', onArrow);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onArrow);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  const pageRef             = useRef(null);
  const toolbarRef          = useRef(null);
  // Si venimos de una posición restaurada, el "scroll inicial a HOY" ya se
  // considera hecho: la restauración corre por su propia rama de abajo.
  const didInitialScroll    = useRef(!!restoredPos);
  const scrollToTodayRef    = useRef(false);
  const scrollToDateRef     = useRef(null);
  // Restauración re-aplicable: cada ciclo de carga reemplaza el DOM de los
  // días (loading ? spinner : días) y resetea el scroll, así que durante una
  // ventana desde el montaje se re-aplica el scroll en cada loading=false.
  const restoreDayRef       = useRef(restoredPos?.day ?? null);
  const restoreUntilRef     = useRef(restoredPos ? Date.now() + 15000 : 0);

  // Scroll a HOY: inicial + tras cambio de mes por botón HOY
  useEffect(() => {
    if (loading) return;
    if (!didInitialScroll.current) {
      didInitialScroll.current = true;
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-day="${todayStr()}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    if (restoreDayRef.current) {
      if (Date.now() > restoreUntilRef.current) {
        restoreDayRef.current = null;
      } else {
        const target = restoreDayRef.current;
        let tries = 0;
        const attempt = () => {
          const el = document.querySelector(`[data-day="${target}"]`);
          if (el) {
            // Instantáneo: 'smooth' se cancela si el DOM se re-renderiza
            // durante la animación (switch de vista, nuevo ciclo de carga).
            el.scrollIntoView({ behavior: 'auto', block: 'start' });
            try { sessionStorage.removeItem(AGENDA_LAST_POS_KEY); } catch {}
          } else if (++tries < 300) {
            requestAnimationFrame(attempt);
          }
        };
        // Primer intento síncrono (los días suelen estar ya renderizados);
        // los reintentos por frame cubren el fetch en curso.
        attempt();
        return;
      }
    }
    if (scrollToDateRef.current) {
      const target = scrollToDateRef.current;
      scrollToDateRef.current = null;
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-day="${target}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    if (scrollToTodayRef.current) {
      scrollToTodayRef.current = false;
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-day="${todayStr()}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading]);

  // Espejo de `month` legible desde el cleanup de desmontaje sin re-suscribir
  // el efecto en cada cambio de mes.
  const monthRef = useRef(month);
  useEffect(() => { monthRef.current = month; }, [month]);

  // Al desmontar (p.ej. navegar a /agenda/eventos/:id/equipos y volver),
  // persistimos mes + día visible para no perder la posición al remontar.
  // useLayoutEffect y no useEffect: su cleanup corre ANTES de que React
  // remueva el DOM de la ruta, así el primer bloque visible sigue consultable.
  useLayoutEffect(() => {
    return () => {
      try {
        const mainEl = document.querySelector('.main-content');
        const containerTop = mainEl ? mainEl.getBoundingClientRect().top : 0;
        const dayEls = mainEl ? [...mainEl.querySelectorAll('[data-day]')] : [];
        // +8px de tolerancia por el borde del strip sticky superpuesto.
        const visible = dayEls.find(el => el.getBoundingClientRect().bottom > containerTop + 8);
        const day = visible?.getAttribute('data-day') || null;
        // Sin día visible (montaje intermedio, vista mobile) no escribimos:
        // pisaríamos una posición válida pendiente de aplicar.
        if (day) {
          sessionStorage.setItem(AGENDA_LAST_POS_KEY, JSON.stringify({
            month: monthRef.current, day, ts: Date.now(),
          }));
        }
      } catch {}
    };
  }, []);

  // Keep --toolbar-height in sync for sticky section offsets
  useEffect(() => {
    if (!toolbarRef.current || !pageRef.current) return;
    const obs = new ResizeObserver(() => {
      const h = Math.ceil(toolbarRef.current.getBoundingClientRect().height);
      pageRef.current.style.setProperty('--toolbar-height', `${h}px`);
    });
    obs.observe(toolbarRef.current);
    return () => obs.disconnect();
  }, []);

  function shiftMonth(delta) {
    const [yr, mo] = month.split('-').map(Number);
    const d = new Date(yr, mo - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setExpandOverrides({});
  }

  function isExpanded(dateStr) {
    if (dateStr in expandOverrides) return expandOverrides[dateStr];
    if (month === currentMonthStr()) {
      const today = todayStr();
      return dateStr >= addDaysStr(today, -10) && dateStr <= addDaysStr(today, 10);
    }
    // Otros meses: expandir solo días con contenido
    return !!(eventsByDate[dateStr]?.length || tasksByDate[dateStr]?.length || armadosByDate[dateStr]?.length || operationsByDate[dateStr]?.length);
  }

  function toggleExpand(dateStr) {
    setExpandOverrides(prev => ({ ...prev, [dateStr]: !isExpanded(dateStr) }));
  }

  // Todos los días calendario del mes visible
  const allDays = useMemo(() => {
    const [yr, mo] = month.split('-').map(Number);
    const lastDay = new Date(yr, mo, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => {
      const d = i + 1;
      return `${yr}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    });
  }, [month]);

  function collapseAll() {
    const overrides = {};
    allDays.forEach(d => { overrides[d] = false; });
    setExpandOverrides(overrides);
  }

  function expandAll() {
    const overrides = {};
    allDays.forEach(d => { overrides[d] = true; });
    setExpandOverrides(overrides);
  }

  function showCurrentWeek() {
    const today = todayStr();
    const reference = new Date(`${today}T12:00:00`);
    const mondayOffset = (reference.getDay() + 6) % 7;
    const weekDays = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(reference);
      date.setDate(reference.getDate() - mondayOffset + index);
      return date.toISOString().slice(0, 10);
    });
    const currentMonth = today.slice(0, 7);
    const [year, monthNumber] = currentMonth.split('-').map(Number);
    const lastDay = new Date(year, monthNumber, 0).getDate();
    const overrides = {};

    for (let day = 1; day <= lastDay; day += 1) {
      const date = `${year}-${String(monthNumber).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      overrides[date] = weekDays.includes(date);
    }

    scrollToDateRef.current = today;
    setMonth(currentMonth);
    setExpandOverrides(overrides);
  }


  function goToToday() {
    const today = todayStr();
    const cm = currentMonthStr();
    if (month !== cm) {
      scrollToTodayRef.current = true;
      setMonth(cm);
      setExpandOverrides({});
    } else {
      setExpandOverrides(prev => ({ ...prev, [today]: true }));
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-day="${today}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  // Filtered events and tasks
  const filteredEvents = useMemo(() => {
    const q = normalize(search);
    return events.filter(e => {
      if (TASK_STATUSES.has(statusFilter)) return false;
      if (statusFilter === 'archivado') { if (!e.archived) return false; }
      else if (statusFilter !== 'todos' && e.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [e.name, e.client, e.place, e.sales_referent, e.notes,
                        e.team_setup, e.team_operation, e.team_breakdown, e.odoo_code]
        .filter(Boolean).join(' ');
      return normalize(haystack).includes(q);
    });
  }, [events, search, statusFilter]);

  const filteredTasks = useMemo(() => {
    const q = normalize(search);
    return tasks.filter(t => {
      if (EVENT_STATUSES.has(statusFilter)) return false;
      if (statusFilter !== 'todos' && t.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [t.title, t.owner, t.notes, t.event_name].filter(Boolean).join(' ');
      return normalize(haystack).includes(q);
    });
  }, [tasks, search, statusFilter]);

  const filteredArmados = useMemo(() => {
    const q = normalize(search);
    return armados.filter(a => {
      if (statusFilter !== 'todos') return false;
      if (!q) return true;
      const assignments = (a.staff_assignments || []).map(item => `${item.person_name || ''} ${item.role || ''}`).join(' ');
      const sections = (a.staff_sections || []).map(s => `${s.title || ''} ${s.name || ''} ${s.note || ''} ${s.time_text || ''}`).join(' ');
      const haystack = [
        a.event_name,
        a.event_place,
        a.place,
        a.client,
        a.sales_referent,
        a.notes,
        a.assigned_people,
        assignments,
        sections,
      ].filter(Boolean).join(' ');
      return normalize(haystack).includes(q);
    });
  }, [armados, search, statusFilter]);

  const eventsByDate = useMemo(() => {
    const m = {};
    filteredEvents.forEach(e => {
      const d = e.event_date?.slice(0, 10);
      if (d) (m[d] = m[d] || []).push(e);
    });
    return m;
  }, [filteredEvents]);

  const tasksByDate = useMemo(() => {
    const m = {};
    filteredTasks.forEach(t => {
      const d = t.task_date?.slice(0, 10);
      if (d) (m[d] = m[d] || []).push(t);
    });
    return m;
  }, [filteredTasks]);

  const armadosByDate = useMemo(() => {
    const m = {};
    filteredArmados.forEach(a => {
      const d = a.armado_date?.slice(0, 10);
      if (d) (m[d] = m[d] || []).push(a);
    });
    return m;
  }, [filteredArmados]);

  const filteredOperations = useMemo(() => {
    const q = normalize(search);
    return operations.filter(op => {
      if (statusFilter !== 'todos') return false;
      if (!q) return true;
      const assignments = (op.staff_assignments || []).map(a => `${a.person_name || ''} ${a.role || ''}`).join(' ');
      const sections = (op.staff_sections || []).map(s => `${s.title || ''} ${s.name || ''} ${s.note || ''} ${s.time_text || ''}`).join(' ');
      const haystack = [op.event_name, op.event_place, op.event_odoo, op.place, op.title, op.note, assignments, sections]
        .filter(Boolean).join(' ');
      return normalize(haystack).includes(q);
    });
  }, [operations, search, statusFilter]);

  const operationsByDate = useMemo(() => {
    const m = {};
    filteredOperations.forEach(op => {
      const d = op.operation_date?.slice(0, 10);
      if (d) (m[d] = m[d] || []).push(op);
    });
    return m;
  }, [filteredOperations]);

  const hasSearch = search.trim() !== '' || statusFilter !== 'todos';

  // La búsqueda dejó de filtrar el mes en pantalla: consulta el backend sin
  // límite de fechas y reemplaza la lista de días por sus resultados.
  const globalSearch = useAgendaSearch({
    query: search,
    dimensions: searchDims,
    includeArchived: searchArchived,
  });

  const toggleSearchDim = (key) => {
    setSearchDims(prev => (
      prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]
    ));
  };

  // Un resultado puede caer en otro mes: primero se mueve la agenda a ese mes y
  // recién después se limpia la búsqueda, para que el día ya exista al llegar.
  const openSearchResult = (row) => {
    if (row.date) {
      const targetMonth = row.date.slice(0, 7);
      if (targetMonth !== month) setMonth(targetMonth);
      setExpandOverrides(prev => ({ ...prev, [row.date]: true }));
      // Respaldo para el cambio de mes: el efecto atado a `loading` scrollea al
      // día cuando termina el reload. No alcanza solo (si el resultado cae en el
      // mes ya visible NO hay reload), de ahí el scroll imperativo de abajo.
      scrollToDateRef.current = row.date;

      const rowAttr = row.kind === 'tarea' ? 'data-task-id'
        : (row.kind === 'evento' ? 'data-event-id' : null);

      // Elemento con caja real al que scrollear. Las filas son display:contents
      // (sin caja propia): scrollIntoView sobre la fila no mueve nada, hay que
      // apuntar a una de sus celdas.
      const findTarget = () => {
        const rowEl = rowAttr ? document.querySelector(`[${rowAttr}="${row.id}"]`) : null;
        const dayEl = document.querySelector(`[data-day="${row.date}"]`);
        const anchor = rowEl || (!rowAttr ? dayEl : null);
        if (!anchor) return null;
        let box = anchor.getBoundingClientRect().height ? anchor : null;
        if (!box) {
          for (const child of anchor.querySelectorAll('*')) {
            const r = child.getBoundingClientRect();
            if (r.height > 0 && r.width > 0) { box = child; break; }
          }
        }
        return { rowEl, box: box || dayEl || anchor, toRow: !!rowEl };
      };
      const scrollNow = () => {
        const t = findTarget();
        if (t) t.box.scrollIntoView({ behavior: 'auto', block: t.toRow ? 'center' : 'start' });
      };

      // La grilla se arma de a poco: un único scroll temprano queda mal porque
      // los días de arriba todavía no ocupan su alto. Se scrollea al encontrar el
      // destino y se re-scrollea unas veces más, ya con el layout asentado.
      let tries = 0;
      const settle = () => {
        const t = findTarget();
        if (t) {
          scrollToDateRef.current = null;
          scrollNow();
          [120, 300, 600].forEach(ms => setTimeout(scrollNow, ms));
          if (t.rowEl) {
            t.rowEl.classList.add('is-just-created');
            setTimeout(() => t.rowEl.classList.remove('is-just-created'), 1800);
          }
        } else if (++tries < 120) {
          requestAnimationFrame(settle);
        }
      };
      requestAnimationFrame(settle);
    }
    setSearch('');
    setSearchOpen(false);
  };

  const visibleDays = useMemo(() => {
    if (!hasSearch) return allDays;
    return allDays.filter(d => (
      eventsByDate[d]?.length ||
      tasksByDate[d]?.length ||
      armadosByDate[d]?.length ||
      operationsByDate[d]?.length
    ));
  }, [hasSearch, allDays, eventsByDate, tasksByDate, armadosByDate, operationsByDate]);

  // Auto-expand: solo abre días con resultados cuando hay búsqueda o filtro activo
  useEffect(() => {
    if (!hasSearch) return;
    const overrides = {};
    allDays.forEach(d => {
      overrides[d] = !!(eventsByDate[d]?.length || tasksByDate[d]?.length || armadosByDate[d]?.length || operationsByDate[d]?.length);
    });
    setExpandOverrides(prev => ({ ...prev, ...overrides }));
  }, [hasSearch, allDays, eventsByDate, tasksByDate, armadosByDate, operationsByDate]);

  function openDrawer(type, target, date, initialData = null) {
    setDrawerType(type);
    setDrawerTarget(target);
    setDrawerDate(date);
    setDrawerInitialData(initialData);
  }

  function openPdfEventProposal({ proposal, warnings }, fallbackDate) {
    const eventDate = proposal?.event_date || fallbackDate;
    if (eventDate) setExpandOverrides(prev => ({ ...prev, [eventDate]: true }));
    openDrawer('event', null, eventDate, { ...proposal, pdfWarnings: warnings || [] });
  }

  function closeDrawer() {
    setDrawerType(null);
    setDrawerTarget(null);
    setDrawerDate(null);
    setDrawerInitialData(null);
  }

  function reload() { load(month); }

  // Issue 1: actualizaciones locales sin reload completo
  function updateEvent(id, patch) {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  }
  function addEventSection(eventId, section) {
    setEvents(prev => prev.map(e => (
      e.id === eventId
        ? { ...e, staff_sections: [...(e.staff_sections || []), section] }
        : e
    )));
  }
  function updateTask(id, patch) {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  }
  function addTask(t) { setTasks(prev => [...prev, t]); }
  function addArmado(a) { setArmados(prev => [...prev, a]); }
  function addOperation(op) { setOperations(prev => [...prev, op]); }
  function addEvent(ev) {
    const evDate  = ev.event_date?.slice(0, 10);
    const evMonth = evDate?.slice(0, 7);
    if (evMonth && evMonth !== month) {
      scrollToDateRef.current = evDate;
      setMonth(evMonth);
      setExpandOverrides({});
    } else {
      setEvents(prev => [...prev, ev]);
      if (evDate) requestAnimationFrame(() => {
        const el = document.querySelector(`[data-day="${evDate}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }
  function updateArmado(id, patch) { setArmados(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a)); }
  function updateOperation(id, patch) { setOperations(prev => prev.map(op => op.id === id ? { ...op, ...patch } : op)); }
  function removeArmado(id) { setArmados(prev => prev.filter(a => a.id !== id)); }
  function removeOperation(id) { setOperations(prev => prev.filter(op => op.id !== id)); }
  function removeTask(id) { setTasks(prev => prev.filter(t => t.id !== id)); }
  function removeEvent(id) {
    setEvents(prev => prev.filter(e => e.id !== id));
    setArmados(prev => prev.filter(a => a.event_id !== id));
    setOperations(prev => prev.filter(op => op.event_id !== id));
  }

  useEffect(() => {
    async function onAuxOperationDeleted(e) {
      const { eventId, type } = e.detail || {};
      if (!eventId || !type) return;
      const matching = operationsRef.current.filter(op => (
        op.event_id === eventId
        && op.type === type
      ));
      if (!matching.length) return;
      setOperations(prev => prev.filter(op => !matching.some(match => match.id === op.id)));
      await Promise.all(matching.map(op => api.delete(`/event-operations/${op.id}`).catch(() => null)));
      load(month, { silent: true });
    }
    window.addEventListener('agenda:aux-operation-deleted', onAuxOperationDeleted);
    return () => window.removeEventListener('agenda:aux-operation-deleted', onAuxOperationDeleted);
  }, [load, month]);

  function scrollToArmado(eventId, armadoDate) {
    const dateStr = String(armadoDate).slice(0, 10);
    setExpandOverrides(prev => ({ ...prev, [dateStr]: true }));
    setTimeout(() => {
      const el = document.querySelector(`[data-armado-event="${eventId}"]`);
      if (el) {
        el.children[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('armado-flash');
        setTimeout(() => el.classList.remove('armado-flash'), 1800);
      }
    }, 80);
  }

  function reorderEvents(date, newOrderedIds) {
    setEvents(prev => {
      const byId = Object.fromEntries(prev.map(e => [e.id, e]));
      const rest = prev.filter(e => e.event_date?.slice(0, 10) !== date);
      const reordered = newOrderedIds.map(id => byId[id]).filter(Boolean);
      return [...rest, ...reordered];
    });
  }

  // Los move: update optimista + persistir. Al confirmar, un reload silencioso
  // garantiza que el que mueve vea el cambio sí o sí (no depende del socket).
  function moveEvent(id, newDate) {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, event_date: newDate } : e));
    api.put(`/events/${id}`, { event_date: newDate })
      .then(() => load(month, { silent: true }))
      .catch(() => load(month));
  }

  function moveTask(id, newDate) {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, task_date: newDate, sort_order: null } : t));
    api.put(`/tasks/${id}`, { task_date: newDate, sort_order: null })
      .then(() => load(month, { silent: true }))
      .catch(() => load(month));
  }

  function moveArmado(id, newDate) {
    setArmados(prev => prev.map(a => a.id === id ? { ...a, armado_date: newDate, sort_order: null } : a));
    api.put(`/armados/${id}`, { armado_date: newDate, sort_order: null })
      .then(() => load(month, { silent: true }))
      .catch(() => load(month));
  }

  function requestMoveEventDate(event) {
    const currentDate = event?.event_date?.slice(0, 10) || todayStr();
    setMoveDateEvent(event);
    setMoveDateTarget(currentDate);
  }

  function closeMoveDateModal() {
    if (moveDateSaving) return;
    setMoveDateEvent(null);
    setMoveDateTarget('');
  }

  async function confirmMoveEventDate() {
    if (!moveDateEvent || !moveDateTarget) return;
    const currentDate = moveDateEvent.event_date?.slice(0, 10);
    if (moveDateTarget === currentDate) return;
    setMoveDateSaving(true);
    try {
      const { data } = await api.post(`/events/${moveDateEvent.id}/move-date`, { event_date: moveDateTarget });
      if (data?.event) {
        setEvents(prev => prev.map(e => e.id === data.event.id ? data.event : e));
      }
      if (Array.isArray(data?.armados)) {
        setArmados(prev => {
          const byId = new Map(data.armados.map(a => [a.id, a]));
          return prev.map(a => byId.get(a.id) || a);
        });
      }
      if (Array.isArray(data?.shifted_operations)) {
        setOperations(prev => {
          const byId = new Map(data.shifted_operations.map(op => [op.id, op]));
          return prev.map(op => byId.get(op.id) || op);
        });
      }
      const targetMonth = moveDateTarget.slice(0, 7);
      if (targetMonth !== month) setMonth(targetMonth);
      setExpandOverrides(prev => ({ ...prev, [moveDateTarget]: true }));
      setMoveDateEvent(null);
      setMoveDateTarget('');
      await load(targetMonth, { silent: true });
    } catch {
      await load(month);
    } finally {
      setMoveDateSaving(false);
    }
  }

  function reorderTasks(date, newOrderedIds) {
    setTasks(prev => {
      const byId = Object.fromEntries(prev.map(t => [t.id, t]));
      const rest = prev.filter(t => t.task_date?.slice(0, 10) !== date);
      const reordered = newOrderedIds.map(id => byId[id]).filter(Boolean);
      return [...rest, ...reordered];
    });
  }

  function reorderArmados(date, newOrderedIds) {
    // Subset-aware: reordena SOLO los ids pasados (armados o desarmados),
    // sustituyéndolos en sus posiciones y dejando el resto (incl. el otro tipo) intacto.
    setArmados(prev => {
      const byId = Object.fromEntries(prev.map(a => [a.id, a]));
      const idSet = new Set(newOrderedIds);
      const queue = newOrderedIds.map(id => byId[id]).filter(Boolean);
      let qi = 0;
      return prev.map(a =>
        (a.armado_date?.slice(0, 10) === date && idSet.has(a.id)) ? queue[qi++] : a
      );
    });
  }

  function openEventOperationDatePicker(event, type, anchorEl = null, options = {}) {
    if (!event?.id || !event.event_date) return;
    const rect = anchorEl?.getBoundingClientRect?.();
    setOperationDatePicker({
      event,
      type,
      replaceExisting: Boolean(options?.replaceExisting),
      replaceSectionId: options?.replaceSectionId || null,
      anchorRect: rect ? {
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      } : null,
    });
  }

  async function createOperationForEvent(selection) {
    if (!operationDatePicker || !selection?.dates?.length) return;
    const { event, type, replaceExisting, replaceSectionId } = operationDatePicker;
    const selectedOperations = selection.operations || selection.dates.map(operationDate => ({ operationDate }));
    const baseEventName = operationBaseEventName(event.name);
    const fallbackTitle = type === 'desarmado'
      ? `Desarmado ${baseEventName}`
      : `Armado ${baseEventName}`;
    try {
      if (replaceExisting) {
        const matching = operationsRef.current.filter(op => (
          op.event_id === event.id
          && op.type === type
        ));
        if (matching.length) {
          setOperations(prev => prev.filter(op => !matching.some(match => match.id === op.id)));
          await Promise.all(matching.map(op => api.delete(`/event-operations/${op.id}`).catch(() => null)));
        }
        if (replaceSectionId) {
          await api.delete(`/staff-assignments/sections/${replaceSectionId}`).catch(() => null);
          setEvents(prev => prev.map(e => (
            e.id === event.id
              ? { ...e, staff_sections: (e.staff_sections || []).filter(s => s.id !== replaceSectionId) }
              : e
          )));
        }
      }
      for (const operation of selectedOperations) {
        const { data } = await api.post('/event-operations', {
          event_id: event.id,
          type,
          operation_date: operation.operationDate,
          place: event.place || null,
          title: operation.title || fallbackTitle,
        });
        addOperation(data);
      }
      const { data: section } = await api.post('/staff-assignments/sections', {
        event_id: event.id,
        phase: type,
        name: `aux-${type}-${Date.now()}`,
        note: selection.statusText,
        kind: 'status',
        sort_order: -1,
      });
      addEventSection(event.id, section);
      setOperationDatePicker(null);
      load(month, { silent: true });
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo crear la operación auxiliar.');
    }
  }

  function selectAgendaView(nextView) {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (nextView === 'general') next.delete('vista');
      else next.set('vista', nextView);
      return next;
    });
  }

  const focusedSection = agendaView === 'general' ? null : agendaView;
  const focusedDays = useMemo(() => {
    if (!focusedSection) return visibleDays;
    return allDays.filter(date => {
      if (focusedSection === 'eventos') return !!eventsByDate[date]?.length;
      if (focusedSection === 'tareas') return !!tasksByDate[date]?.length;
      return (armadosByDate[date] || []).length > 0 || (operationsByDate[date] || []).length > 0;
    });
  }, [focusedSection, visibleDays, allDays, eventsByDate, tasksByDate, armadosByDate, operationsByDate]);

  const renderAgendaDay = (date, { onHorizontalScroll } = {}) => (
    <DayBlock
      key={date}
      date={date}
      showColumnHeader={!focusedSection}
      visibleSections={focusedSection === 'logistica' ? ['armados', 'desarmados'] : focusedSection ? [focusedSection] : null}
      forceExpandedSections={Boolean(focusedSection)}
      onHorizontalScroll={onHorizontalScroll}
      events={eventsByDate[date] || []}
      tasks={tasksByDate[date] || []}
      armados={armadosByDate[date] || []}
      operations={operationsByDate[date] || []}
      isExpanded={isExpanded(date)}
      onToggle={() => toggleExpand(date)}
      canOperate={canOperate}
      canAdmin={canAdmin}
      canViewEventEquipment={canViewEventEquipment}
      onLoad={reload}
      onEditEvent={ev => openDrawer('event', ev, null)}
      onEditTask={t => openDrawer('task', t, null)}
      onUpdateEvent={updateEvent}
      onUpdateTask={updateTask}
      onReorderEvents={reorderEvents}
      onReorderTasks={reorderTasks}
      onReorderArmados={reorderArmados}
      onRequestMoveEventDate={requestMoveEventDate}
      onCreateEventOperation={openEventOperationDatePicker}
      onAddArmado={addArmado}
      onAddEvent={addEvent}
      onAddTask={addTask}
      onPdfEventProposal={openPdfEventProposal}
      onUpdateArmado={updateArmado}
      onUpdateOperation={updateOperation}
      onRemoveArmado={removeArmado}
      onRemoveOperation={removeOperation}
      onRemoveTask={removeTask}
      onRemoveEvent={removeEvent}
      kbFocus={kbFocus}
      onScrollToArmado={scrollToArmado}
      onOpenChecklist={(cid, eid) => { setChecklistPanelId(cid); setChecklistPanelEventId(eid); }}
      personnel={personnel}
      lugares={lugares}
      clients={clients}
      salesRefs={salesRefs}
      monthConflicts={monthConflicts}
      assignmentModel={assignmentModel}
      assignmentModelError={assignmentModelError}
      retryAssignmentModel={retryAssignmentModel}
    />
  );

  if (isResponsiveAgenda) {
    return (
      <AgendaResponsiveView
        month={month}
        setMonth={setMonth}
        loading={loading}
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        globalSearch={globalSearch}
        searchDimensions={SEARCH_DIMENSIONS}
        activeDimensions={searchDims}
        onToggleDimension={toggleSearchDim}
        onOpenSearchResult={openSearchResult}
        allDays={allDays}
        eventsByDate={eventsByDate}
        tasksByDate={tasksByDate}
        armadosByDate={armadosByDate}
        operationsByDate={operationsByDate}
        canAdmin={canAdmin}
        canOperate={canOperate}
        canViewEventEquipment={canViewEventEquipment}
        canEditEventEquipment={canEditEventEquipment}
        onRefresh={reload}
        onAddEvent={addEvent}
        onUpdateEvent={updateEvent}
        onAddOperation={addOperation}
        onAddEventSection={addEventSection}
        personnel={personnel}
        lugares={lugares}
        clients={clients}
        salesRefs={salesRefs}
        assignmentModel={assignmentModel}
        assignmentModelError={assignmentModelError}
        retryAssignmentModel={retryAssignmentModel}
      />
    );
  }

  return (
    <div className="agenda-page" ref={pageRef}>
      {/* ── Toolbar ── */}
      <div className="ag-toolbar" ref={toolbarRef}>
        <div className="page-header module-sections-header ag-topbar">
          <div className="ag-heading">
            <h1 className="page-title module-sections-title ag-title">Eventos</h1>
            <p className="page-subtitle module-sections-subtitle ag-subtitle">Plan operativo por fecha</p>
            <AgendaSyncStatus />
          </div>
          <div className="ag-actions">
            <button
              className={`ag-btn ag-search-toggle${searchOpen ? ' active' : ''}`}
              type="button"
              onClick={() => setSearchOpen(o => !o)}
              aria-label="Buscar"
              title="Buscar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
            {canViewEquipment && (
              <button className="btn btn-ghost ag-btn" type="button" onClick={() => navigate('/equipos')}>
                <span className="ag-btn-full">Vista de Equipos</span>
                <span className="ag-btn-short">Equipos</span>
              </button>
            )}
            {canAdmin && (
              <button className="btn btn-primary ag-btn" type="button" onClick={() => openDrawer('event', null, todayStr())}>
                <span className="ag-btn-full">+ Agregar evento</span>
                <span className="ag-btn-short">+ Evento</span>
              </button>
            )}
          </div>
        </div>

        <AgendaSectionTabs value={agendaView} onChange={selectAgendaView} />

        <div className={`ag-filters${searchOpen ? ' ag-filters--search-open' : ''}`}>
          <div className="ag-filter-group ag-filter-group--search">
            <input
              className="input ag-search"
              type="search"
              placeholder="Buscar en toda la agenda…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {search.trim() !== '' && (
            <div className="ag-filter-group ag-search-chips">
              {SEARCH_DIMENSIONS.map(dim => (
                <button
                  key={dim.key}
                  type="button"
                  className={`ag-chip${searchDims.includes(dim.key) ? ' is-on' : ''}`}
                  aria-pressed={searchDims.includes(dim.key)}
                  onClick={() => toggleSearchDim(dim.key)}
                >{dim.label}</button>
              ))}
              <span className="ag-chip-sep" />
              <button
                type="button"
                className={`ag-chip${searchArchived ? ' is-on' : ''}`}
                aria-pressed={searchArchived}
                onClick={() => setSearchArchived(v => !v)}
              >Archivados</button>
            </div>
          )}
          <div className="ag-filter-group">
            <select
              className="input ag-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="todos">Todos los estados</option>
              <optgroup label="— Eventos —">
                <option value="confirmado">Confirmado</option>
                <option value="posible">Posible</option>
                <option value="cancelado">Cancelado</option>
                <option value="archivado">Archivado</option>
              </optgroup>
              <optgroup label="— Tareas —">
                <option value="pendiente">Pendiente</option>
                <option value="finalizado">Finalizado</option>
              </optgroup>
            </select>
            <button className="btn btn-ghost ag-btn" type="button" onClick={collapseAll}>Comprimir</button>
            <button className="btn btn-ghost ag-btn" type="button" onClick={expandAll}>Ver todo</button>
          </div>
          <div className="ag-filter-group ag-date-navigation" aria-label="Navegación mensual">
            <button className="btn btn-ghost ag-btn ag-btn-icon" type="button" onClick={() => shiftMonth(-1)} aria-label="Mes anterior">‹</button>
            <span className="ag-month-label">{monthLabel(month)}</span>
            <button className="btn btn-ghost ag-btn ag-btn-icon" type="button" onClick={() => shiftMonth(1)} aria-label="Mes siguiente">›</button>
            <button className="btn btn-ghost ag-btn" type="button" onClick={showCurrentWeek}>Ver semana</button>
            <button className="btn btn-primary ag-btn" type="button" onClick={goToToday}>Ver hoy</button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {globalSearch.active ? (
        <AgendaSearchResults
          query={search}
          results={globalSearch.results}
          total={globalSearch.total}
          loading={globalSearch.loading}
          loadingMore={globalSearch.loadingMore}
          error={globalSearch.error}
          hasMore={globalSearch.hasMore}
          onLoadMore={globalSearch.loadMore}
          onOpen={openSearchResult}
        />
      ) : loading ? (
        <div className="ag-spinner-wrap"><div className="spinner" /></div>
      ) : agendaView === 'general' ? (
        <div className="agenda-days">
          {visibleDays.map(date => renderAgendaDay(date))}
        </div>
      ) : (
        <AgendaFocusedView
          view={agendaView}
          days={focusedDays}
          renderDay={renderAgendaDay}
        />
      )}

      <AgendaMoveDateModal
        event={moveDateEvent}
        targetDate={moveDateTarget}
        relatedArmados={moveDateEvent ? armados.filter(a => a.event_id === moveDateEvent.id) : []}
        relatedOperations={moveDateEvent ? operations.filter(op => op.event_id === moveDateEvent.id) : []}
        onTargetDate={setMoveDateTarget}
        onCancel={closeMoveDateModal}
        onConfirm={confirmMoveEventDate}
        saving={moveDateSaving}
      />

      {drawerType === 'event' && (
        <EventDrawer
          event={drawerTarget}
          defaultDate={drawerDate}
          initialData={drawerInitialData}
          onClose={closeDrawer}
          onSaved={reload}
        />
      )}
      {drawerType === 'task' && (
        <TaskDrawer
          task={drawerTarget}
          defaultDate={drawerDate}
          onClose={closeDrawer}
          onSaved={reload}
        />
      )}
      {operationDatePicker && (
        <OperationDatePickerPopover
          event={operationDatePicker.event}
          type={operationDatePicker.type}
          replaceExisting={operationDatePicker.replaceExisting}
          anchorRect={operationDatePicker.anchorRect}
          onCancel={() => setOperationDatePicker(null)}
          onConfirm={createOperationForEvent}
        />
      )}

      {checklistPanelId !== null && createPortal(
        <div className="cl-overlay" onClick={closeChecklistPanel}>
          <div
            className="cl-panel-drawer"
            ref={drawerRef}
            onClick={e => e.stopPropagation()}
            onPointerDown={drawerPointerDown}
            onPointerMove={drawerPointerMove}
            onPointerUp={drawerPointerEnd}
            onPointerLeave={drawerPointerEnd}
            onPointerCancel={drawerPointerEnd}
          >
            <button className="cl-panel-close" onClick={closeChecklistPanel}>✕</button>
            <ChecklistEventSidebar
              currentId={checklistPanelId}
              currentEventId={checklistPanelEventId}
              onSelect={(cid, eid) => { setChecklistPanelId(cid); if (eid) setChecklistPanelEventId(eid); }}
            />
            <div className="cl-panel-scroll">
              <ChecklistPage
                checklistId={checklistPanelId}
                panelMode={true}
                onTabSwitch={setChecklistPanelId}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function weekdayLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  const label = new Intl.DateTimeFormat('es-UY', { weekday: 'long' }).format(d);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function shortDateLabel(dateStr) {
  if (!dateStr) return '';
  const [, month, day] = dateStr.split('-');
  return `${day}/${month}`;
}

function statusDateLabel(dateStr) {
  if (!dateStr) return '';
  const [, , day] = dateStr.split('-');
  return `${weekdayLabel(dateStr)} ${Number(day)}`.toUpperCase();
}

function statusSingleDateLabel(dateStr) {
  if (!dateStr) return '';
  const [, , day] = dateStr.split('-');
  return `${weekdayLabel(dateStr)} ${Number(day)}`.toUpperCase();
}

function OperationDatePickerPopover({ event, type, replaceExisting = false, anchorRect, onCancel, onConfirm }) {
  const popoverRef = useRef(null);
  const eventDate = event.event_date?.slice(0, 10);
  const title = type === 'desarmado' ? 'Desarmado posterior' : 'Armado previo';
  const rawDates = Array.from({ length: 7 }, (_, i) => (
    type === 'desarmado'
      ? addDaysStr(eventDate, i + 1)
      : addDaysStr(eventDate, -(i + 1))
  ));
  const orderedDates = rawDates;
  const [selectedDate, setSelectedDate] = useState(rawDates[0]);
  const [selectedArmadoDates, setSelectedArmadoDates] = useState(() => new Set([rawDates[0]]));
  const popoverHeight = type === 'armado' ? 318 : 306;
  const anchorTop = anchorRect?.top || 160;
  const anchorBottom = anchorRect?.bottom || anchorTop;
  const spaceBelow = window.innerHeight - anchorBottom - 10;
  const openUp = spaceBelow < popoverHeight && anchorTop > popoverHeight + 10;
  const rawTop = openUp ? anchorTop - popoverHeight - 6 : anchorBottom + 6;
  const top = Math.max(10, Math.min(rawTop, window.innerHeight - popoverHeight - 10));
  const left = Math.max(12, Math.min((anchorRect?.left || 320), window.innerWidth - 292));

  useEffect(() => {
    function onMouseDown(e) {
      if (popoverRef.current?.contains(e.target)) return;
      onCancel();
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onCancel]);

  return createPortal(
    <div
      ref={popoverRef}
      className="operation-date-popover"
      style={{ position: 'fixed', top, left, zIndex: 9999 }}
    >
      <div className="operation-date-popover-head">
        <div>
          <strong>{title}</strong>
          <small>Evento: {weekdayLabel(eventDate)} {shortDateLabel(eventDate)}</small>
        </div>
        <button type="button" onClick={onCancel} aria-label="Cerrar">×</button>
      </div>
      <div className="operation-date-popover-event">{event.name}</div>
      <div className={`operation-date-options${type === 'armado' ? ' operation-date-options--week' : ''}`}>
        {orderedDates.map(date => (
          <button
            key={date}
            type="button"
            className={(type === 'armado' ? selectedArmadoDates.has(date) : date === selectedDate) ? 'selected' : ''}
            onClick={() => {
              if (type !== 'armado') {
                setSelectedDate(date);
                return;
              }
              setSelectedArmadoDates(prev => {
                const next = new Set(prev);
                if (next.has(date) && next.size > 1) next.delete(date);
                else next.add(date);
                return next;
              });
            }}
          >
            <span>{type === 'armado' ? weekdayLabel(date).slice(0, 3) : weekdayLabel(date)}</span>
            <strong>{shortDateLabel(date)}</strong>
          </button>
        ))}
      </div>
      {type === 'armado' && (
        <div className="operation-date-hint">Elegí uno o varios días de armado.</div>
      )}
      <button
        type="button"
        className="operation-date-create"
        onClick={() => {
          const selectedDates = type === 'armado'
            ? orderedDates.filter(date => selectedArmadoDates.has(date))
            : [selectedDate];
          const first = selectedDates[0];
          const last = selectedDates[selectedDates.length - 1];
          const allSelectedAreContiguous = selectedDates.every((date, idx) => idx === 0 || date === addDaysStr(selectedDates[idx - 1], 1));
          const statusDetail = selectedDates.length === 1
            ? `(${statusDateLabel(first)})`
            : allSelectedAreContiguous
              ? `(${statusDateLabel(first)} AL ${statusDateLabel(last)})`
              : `(${selectedDates.map(statusDateLabel).join(', ')})`;
          const statusText = type === 'desarmado'
            ? `DESARMADO EL ${statusSingleDateLabel(first)}`
            : `YA ESTA ARMADO\n${statusDetail}`;
          const total = selectedDates.length;
          const baseEventName = operationBaseEventName(event.name);
          const operations = selectedDates.map((operationDate, idx) => ({
            operationDate,
            title: type === 'armado'
              ? total > 1
                ? `Armado ${baseEventName} - Día ${idx + 1} de ${total}`
                : `Armado ${baseEventName}`
              : `Desarmado ${baseEventName}`,
          }));
          onConfirm({ dates: selectedDates, operations, statusText });
        }}
      >
        {replaceExisting ? 'Guardar cambio' : 'Crear'}
      </button>
    </div>,
    document.body
  );
}

function ChecklistEventSidebar({ currentId, currentEventId, onSelect }) {
  const [events, setEvents] = useState([]);
  const today = todayStr();
  const activeRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fmt = d => d.toISOString().slice(0, 10);
    const from = new Date(); from.setDate(from.getDate() - 30);
    const to = new Date(); to.setDate(to.getDate() + 90);
    api.get(`/checklists/planning?from=${fmt(from)}&to=${fmt(to)}`)
      .then(r => setEvents(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const btn = activeRef.current;
      const container = scrollRef.current;
      const btnTop = btn.offsetTop;
      const btnBottom = btnTop + btn.offsetHeight;
      const visibleTop = container.scrollTop;
      const visibleBottom = visibleTop + container.clientHeight;
      if (btnTop < visibleTop || btnBottom > visibleBottom) {
        container.scrollTop = btnTop - container.clientHeight / 2 + btn.offsetHeight / 2;
      }
    }
  }, [events, currentEventId]);

  const byDate = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const d = (ev.event_date || '').slice(0, 10);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(ev);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  async function handleClick(ev) {
    if (ev.checklist) {
      onSelect(ev.checklist.id, ev.id);
    } else {
      try {
        const { data } = await api.post('/checklists', { event_id: ev.id, type: 'CARGA' });
        onSelect(data.id, ev.id);
      } catch { /* ignore */ }
    }
  }

  return (
    <div className="cl-panel-sidebar">
      <div className="cl-panel-sidebar-title">Eventos</div>
      <div className="cl-panel-sidebar-scroll" ref={scrollRef}>
        {byDate.map(([date, evs]) => {
          const isPastDay = date < today;
          return (
            <div key={date} className={`cl-panel-sidebar-day${isPastDay ? ' past-day' : ''}`}>
              <div className="cl-panel-sidebar-day-label">{fmtSidebarDate(date)}</div>
              {evs.map(ev => {
                const active = ev.id === currentEventId;
                const isPast = date < today;
                return (
                  <button
                    key={ev.id}
                    ref={active ? activeRef : null}
                    className={`cl-panel-sidebar-event${active ? ' active' : ''}${isPast ? ' past' : ''}${!ev.checklist ? ' no-checklist' : ''}`}
                    onClick={() => handleClick(ev)}
                  >
                    <div className="cl-panel-sidebar-name">{ev.name}</div>
                    {ev.place && <div className="cl-panel-sidebar-place">{ev.place}</div>}
                    {(ev.start_time || ev.end_time) && (
                      <div className="cl-panel-sidebar-time">
                        {ev.start_time?.slice(0, 5)}
                        {ev.end_time && <> → {ev.end_time.slice(0, 5)}</>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function fmtSidebarDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
}
